import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGlobalGrid, getGlobalGridContainerWidth } from 'src/store/globalGrid/selectors';
import { Actions } from 'src/store/globalGrid/actions';

export const useGlobalGrid = (containerElementRef?: React.RefObject<HTMLElement>) => {
  const dispatch = useDispatch();
  const globalGrid = useSelector(getGlobalGrid);
  const gridContainerWidth = useSelector(getGlobalGridContainerWidth);

  const setGridContainerWidth = useCallback((width: number) => {
    dispatch(Actions.setGridContainerWidth(width));
  }, [dispatch]);

  const getGridsWidth = useCallback((gridsCount: number, noGap: boolean = false) => {
    const oneGridWidth = Array.from(Array(gridsCount).keys())
      .reduce((acc) => acc + (!gridContainerWidth ? globalGrid.oneGridWidth : ((gridContainerWidth / globalGrid.gridCount) - globalGrid.gapWidth)), 0);
    if (noGap) {
      return oneGridWidth;
    }
    const gapWidth = gridsCount > 1 ? (gridsCount - 1) * globalGrid.gapWidth : globalGrid.gapWidth;
    return oneGridWidth + gapWidth || 0;
  }, [globalGrid, gridContainerWidth]);

  useEffect(() => {
    const elem = containerElementRef?.current;
    if (!elem) return () => {};

    const handleResize = () => {
      const rect = elem.getBoundingClientRect();
      const style = getComputedStyle(elem);

      const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      setGridContainerWidth(rect.width - paddingX);
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(elem);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerElementRef]);

  return {
    globalGrid,
    getGridsWidth,
    setGridContainerWidth,
  };
};
